import { ColumnsType } from "antd/es/table";
import { useEffect, useMemo, useRef } from "react";
import { DataGridHandle } from "react-data-grid";
import "react-data-grid/lib/styles.css";
import { useTranslation } from "react-i18next";
import useTableData from "../../app/hooks/useTableData";
import useWindowSize from "../../app/hooks/useWindowSize";
import { CompanyActivity } from "../../app/model/company";
import { useAppDispatch, useAppSelector } from "../../app/store";
import { setPage } from "../../app/store/reducers/appSlice";
import { Pagination } from "../pagination";
import ImageFormatter from "./formatters/image-formatter";
import LinkFormatter from "./formatters/link-formatter";
import { GridContainer, StyledDataGrid } from "./styles";

interface GridProps {
  loading?: boolean
}

export default function Grid({ loading }: GridProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { getData: getTableData } = useTableData()
  const { width } = useWindowSize()

  const ref = useRef<DataGridHandle>(null)

  const { grid: data, pagination } = useAppSelector(store => store.AppSlice)

  const columns = useMemo((): ColumnsType<any> => {
    const columns: ColumnsType<any> = [
      { key: "urlLogoFromWeb", dataIndex: "urlLogoFromWeb", align: "center", className: "logo", title: t("Logo"), width: width <= 578 ? 100 : 150, render: (value, row) => <ImageFormatter value={value} isNew={row.isNewFlag} /> },
      { key: "name", dataIndex: "name", align: "center", title: t("CompanyName"), render: (value, row) => <LinkFormatter link={row.urlDetail} value={value} /> },
      { key: "activities", dataIndex: "activities", align: "center", title: t("Activities"), render: (value: CompanyActivity[]) => value.map(item => item.name).join(", ") },
      { key: "country", dataIndex: "country", align: "center", title: t("Country") },
      { key: "stend", dataIndex: "stend", align: "center", title: t("StandNumber") },
    ]

    if (width >= 1024) {
      return columns
    }

    if (width >= 768) {
      return [columns[0], columns[1], columns[2], columns[4]]
    }

    if (width >= 578) {
      return [columns[0], columns[1], columns[2]]
    }

    return [columns[0], columns[1]]
  }, [t, width])

  const handleNavigation = (page: number) => {
    dispatch(setPage(page))
    getTableData({ page })
  }

  useEffect(() => {
    if (!ref.current) return

    const cssColumns = ref.current.element?.style.gridTemplateColumns.split(" ")
    if (!cssColumns) return

    cssColumns[3] = "auto"
    ref.current.element?.style.setProperty("grid-template-columns", cssColumns.join(" "))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current])

  return (
    <GridContainer>
      <StyledDataGrid
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={false}
        rowClassName={(row: any) => row?.vip ? "vip" : ""}
        bordered
      />
      {(pagination && pagination.pageCount > 1) && (
        <Pagination
          {...pagination!}
          onNavigate={handleNavigation}
        />
      )}
    </GridContainer>
  )
}
